import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import { sysColors } from "@/assets/theme/colors";

import classNames from "classnames";
import ChartSkeleton from "@/components/Skeleton/ChartSkeleton";
import ChartLegend from "./ChartLegend";
import ChartTooltip from "./ChartTooltip";

export type LineChartProps = {
  // Chart data
  data: any[];
  lines: { dataKey: string; name: string; color: string }[];
  isCurrency?: boolean;

  // Content
  title: string;
  subtitle?: string;

  // State
  loading?: boolean;

  // Layout
  className?: string;
};

const LineChartComponent = ({
  data,
  lines,
  isCurrency = false,
  title,
  subtitle,
  loading = false,
  className
}: LineChartProps) => {
  return (
    <div
      className={classNames(
        "flex flex-col items-start w-full h-full",
        "border border-sys-brand-outline rounded-lg p-6",
        className
      )}
    >
      {/* Header */}
      <div className="w-full mb-6">
        <p className="text-title-medium text-sys-brand-on-surface">{title}</p>
        {subtitle && <p className="text-body-medium text-sys-brand-on-surface-variant">{subtitle}</p>}
      </div>

      {/* Chart */}
      <div className="w-full">
        {loading ? (
          <ChartSkeleton type="line" />
        ) : (
          <>
            <ResponsiveContainer width="100%" height={225}>
              <LineChart
                data={data}
                margin={{
                  top: 5,
                  right: 30,
                  left: 20,
                  bottom: 5
                }}
              >
                <CartesianGrid strokeDasharray="0 0" stroke={sysColors["sys-brand-surface-container-high"]} />
                <XAxis
                  dataKey="name"
                  tickLine={false}
                  stroke={sysColors["sys-brand-surface-container-high"]}
                  tick={{ fontSize: 12, fill: sysColors["sys-brand-on-surface-variant"] }}
                />
                <YAxis
                  tickLine={false}
                  stroke={sysColors["sys-brand-surface-container-high"]}
                  tick={{ fontSize: 12, fill: sysColors["sys-brand-on-surface-variant"] }}
                />
                <Tooltip
                  content={({ active = false, payload }) => {
                    const items =
                      payload?.map((entry: any) => ({
                        name: entry.name,
                        value: entry.value,
                        color: entry.color
                      })) || [];

                    const subtitle = payload?.[0]?.payload?.name;

                    return (
                      <ChartTooltip
                        active={active}
                        title={title}
                        subtitle={subtitle}
                        items={items}
                        formatAsCurrency={isCurrency}
                      />
                    );
                  }}
                />
                {lines.map((line) => (
                  <Line
                    key={line.dataKey}
                    type="monotone"
                    dataKey={line.dataKey}
                    stroke={line.color}
                    strokeWidth={2}
                    name={line.name}
                    dot={{ r: 3, fill: line.color }}
                    activeDot={{ r: 3, fill: line.color }}
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>

            {/* Legend */}
            <ChartLegend
              items={lines.map((line) => ({
                name: line.name,
                color: line.color,
                visible: true
              }))}
            />
          </>
        )}
      </div>
    </div>
  );
};

export default LineChartComponent;
